import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { filter, map, tap, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  ApplyTagWithFieldSelectDialog
} from 'app/field-shared/dialogs/apply-tag-with-field-select/apply-tag-with-field-select.dialog';
import { invalidateApolloCacheFor } from 'core/apollo-cache-invalidator';
import { AddAssessmentDialog } from 'entity/dialogs/add-assessment/add-assessment.dialog';
import { AddConnectionDialog } from 'entity/dialogs/add-connection/add-connection.dialog';
import { MergeEntitiesDialog } from 'entity/dialogs/merge-entities/merge-entities.dialog';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import { EntityQuickEditDialog } from 'entity/dialogs/quick-edit/entity-quick-edit.dialog';

import { GetCampaignEntityTypeConfigurationGQL, GetEntityBasicGQL, RemoveEntityFromCampaignGQL } from 'generated/graphql';
import { Store } from '@ngrx/store';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ContactTrackingDialog } from 'app/contact-tracking/dialogs/contact-tracking/contact-tracking.dialog';
import { isNilOrEmpty } from 'helpers';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';

@Component({
  selector: 'app-entity-action-box',
  templateUrl: './entity-action-box.component.html',
  styles: ['.mat-list-base[dense] .mat-list-item .mat-icon.fa-comments-question { width: 24px; height: 24px; font-size: 20px; }']
})
export class EntityActionBoxComponent
  extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnChanges, OnInit {
  @Input() buttonsOnly = false;
  @Input() showRemove = true;
  @Input() showMerge = false;

  @Input() campaignId: string;
  @Input() entityId: string;

  entityTypeId: string;


  /** Only true when the entity type has a contact form */
  addContactEnabled = false;

  private _bottomSheetRef: MatBottomSheetRef;

  isMobileView = true;
  campaignHasConnections = true;

  constructor(
    private _breakpointObserver: BreakpointObserver,
    private _removeEntityFromCampaignGQL: RemoveEntityFromCampaignGQL,
    private _getEntityGQL: GetEntityBasicGQL,
    private _getCampaignEntityTypeConfigGQL: GetCampaignEntityTypeConfigurationGQL,
    private _router: Router,
    private _dialog: MatDialog,
    private _sheet: MatBottomSheet,
    private _cd: ChangeDetectorRef,
    private _store: Store

  ) {
    super();
    // https://material.io/design/layout/responsive-layout-grid.html#breakpoints
    // https://v9.material.angular.io/cdk/layout/overview
    this._breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small
    ]).subscribe(result => {
      this.isMobileView = result.matches;
      this._cd.markForCheck();
    });
  }

  ngOnInit(): void {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId || !!entityId),
      switchMap(({ campaignId, entityId }) => this._getEntityConfig(entityId, campaignId))
    ).subscribe(({ data }) => {
      this.addContactEnabled = !isNilOrEmpty(data?.getCampaignEntityTypeConfiguration?.customForm?.fields);
      this._cd.markForCheck();
    });

    this._store.select(selectCurrentCampaign).pipe(
      takeUntil(this.disposer),
      map(c => c.entityConnectionTypes?.length > 0)
    ).subscribe(hasConnections => {
      this.campaignHasConnections = hasConnections;
    });
  }

  _getEntityConfig(entityId: string, campaignId: string) {
    return this._getEntityGQL.fetch({
      entityId,
      campaignId
    }).pipe(
      map(({ data }) => data?.getEntity?.entityTypeId),
      tap((entityTypeId) => {
        this.entityTypeId = entityTypeId;
        this._cd.markForCheck();
      }),
      switchMap((entityTypeId) => this._getCampaignEntityTypeConfigGQL.fetch({
        campaignId: this.campaignId,
        entityTypeId
      }))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  openAddFieldsDialog() {
    this._getEntityGQL.fetch({
      entityId: this.entityId,
      campaignId: this.campaignId
    }).pipe(
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      ApplyTagWithFieldSelectDialog.open(this._dialog, {
        data: {
          campaignId: entity.campaignContextId,
          targetType: 'EntityType',
          targetTypeId: entity.entityTypeId,
          targetId: entity.id
        }
      });
    });
  }

  openAssessmentDialog() {
    this._bottomSheetRef?.dismiss();

    AddAssessmentDialog.open(this._dialog, {
      data: {
        entityTypeId: this.entityTypeId,
        entityId: this.entityId,
        campaignId: this.campaignId
      }
    });
  }

  openAddContactDialog() {
    this._bottomSheetRef?.dismiss();

    ContactTrackingDialog.open(this._dialog, {
      data: {
        entityId: this.entityId,
        campaignId: this.campaignId
      }
    });
  }

  openAddConnectionDialog() {
    this._getEntityGQL.fetch({
      entityId: this.entityId,
      campaignId: this.campaignId
    }).pipe(
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      AddConnectionDialog.open(this._dialog, {
        data: {
          fromEntityId: entity.id,
          campaignId: entity.campaignContextId,
          fromEntityTypeId: entity.entityTypeId
        }
      });
    });
  }

  openMergeEntitiesDialog() {
    this._getEntityGQL.fetch({
      entityId: this.entityId,
      campaignId: this.campaignId
    }).pipe(
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      MergeEntitiesDialog.open(this._dialog, {
        data: {
          entityId: entity.id,
          campaignId: entity.campaignContextId,
          entityTypeId: entity.entityTypeId
        }
      });
    });
  }

  openInBottomSheet(templateRef: TemplateRef<any>) {
    this._bottomSheetRef = this._sheet.open(templateRef);
  }

  // AB-544
  openQuickEditDialog() {
    this._getEntityGQL.fetch({
      entityId: this.entityId,
      campaignId: this.campaignId
    }).pipe(
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      EntityQuickEditDialog.open(this._dialog, {
        data: {
          entityId: entity.id,
          campaignId: entity.campaignContextId
        }
      });
    });
  }

  onRemoveFromCampaignClicked() {
    const dialogRef = ConfirmDialogComponent.open(this._dialog, {
      data: {
        message: 'Are you sure you want to remove this entity from the campaign?'
      }
    });

    dialogRef.afterClosed().pipe(
      filter(proceed => proceed),
      switchMap(() => this._removeEntityFromCampaignGQL.mutate({
        input: {
          campaignId: this.campaignId,
          entityId: this.entityId
        }
      }, {
        update: (proxy, result) => {
          invalidateApolloCacheFor(proxy, c => c.fieldName?.includes('entitiesQuery'));
        }
      }))
    ).subscribe((_) => {
      this._router.navigate(['/entity/list'], {
        queryParams: {
          entityId: null
        },
        queryParamsHandling: 'merge'
      });
    });
  }
}
